import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Paper, Typography } from "@mui/material";
import api from "../../../config/Api";
import { BannerType, HomeBanner } from "../../../types";

const CustomerLayout = () => {
  const navigate = useNavigate();
  const [leftAds, setLeftAds] = useState<HomeBanner[]>([]);
  const [rightAds, setRightAds] = useState<HomeBanner[]>([]);

  useEffect(() => {
    const fetchAds = async () => {
      const token = localStorage.getItem("jwt");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        const [leftAdsRes, rightAdsRes] = await Promise.allSettled([
          api.get(`/home/banners/ads/${BannerType.LEFT_AD}`, { headers }),
          api.get(`/home/banners/ads/${BannerType.RIGHT_AD}`, { headers }),
        ]);

        setLeftAds(leftAdsRes.status === "fulfilled" ? leftAdsRes.value.data || [] : []);
        setRightAds(rightAdsRes.status === "fulfilled" ? rightAdsRes.value.data || [] : []);
      } catch (error) {
        console.error("Failed to load side ad banners:", error);
      }
    };

    fetchAds();
  }, []);

  const handleBannerClick = (targetUrl?: string) => {
    if (targetUrl) {
      if (targetUrl.startsWith("http")) {
        window.location.href = targetUrl;
      } else {
        navigate(targetUrl);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex justify-center py-4 px-2 sm:px-4">
      <div className="w-full max-w-[1600px] flex gap-4">

        {/* LEFT AD BANNERS */}
        {leftAds.length > 0 && (
          <aside className="hidden lg:block w-48 xl:w-56 flex-shrink-0 space-y-4 sticky top-4 h-[calc(100vh-2rem)]">
            {leftAds.slice(0, 2).map((ad, idx) => (
              <Paper
                key={ad.id || idx}
                onClick={() => handleBannerClick(ad.targetUrl)}
                className={`overflow-hidden cursor-pointer rounded-lg flex flex-col justify-between shadow-md transition-transform hover:scale-[1.01] bg-white ${
                  leftAds.length === 1 ? "h-full" : "h-[calc(50%-0.5rem)]"
                }`}
              >
                {/* Clean Image Area */}
                <div className="relative flex-1 w-full min-h-0 overflow-hidden bg-gray-100">
                  <img
                    src={ad.imageUrl}
                    alt={ad.title || "Ad Banner"}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wider bg-black/60 text-white px-2 py-0.5 rounded backdrop-blur-sm">
                    Ad
                  </span>
                </div>

                {/* Content & Action Below Image */}
                <div className="p-3 bg-white flex flex-col justify-between border-t border-gray-100">
                  <div>
                    <Typography variant="subtitle1" className="font-bold leading-tight text-gray-900 line-clamp-2">
                      {ad.title}
                    </Typography>
                    {ad.subtitle && (
                      <Typography variant="body2" className="mt-1 text-gray-600 text-xs line-clamp-2">
                        {ad.subtitle}
                      </Typography>
                    )}
                    {ad.promoCode && (
                      <span className="mt-2 text-[11px] bg-yellow-100 text-yellow-800 border border-yellow-300 font-medium px-2 py-0.5 rounded inline-block">
                        Code: {ad.promoCode}
                      </span>
                    )}
                  </div>
                  <button className="w-full bg-gray-900 text-white font-medium py-1.5 px-3 rounded text-xs hover:bg-gray-800 transition mt-3">
                    Shop Now
                  </button>
                </div>
              </Paper>
            ))}
          </aside>
        )}

        {/* CENTER CONTENT AREA - ALL PAGES RENDER HERE */}
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>

        {/* RIGHT AD BANNERS */}
        {rightAds.length > 0 && (
          <aside className="hidden lg:block w-48 xl:w-56 flex-shrink-0 space-y-4 sticky top-4 h-[calc(100vh-2rem)]">
            {rightAds.slice(0, 2).map((ad, idx) => (
              <Paper
                key={ad.id || idx}
                onClick={() => handleBannerClick(ad.targetUrl)}
                className={`overflow-hidden cursor-pointer rounded-lg flex flex-col justify-between shadow-md transition-transform hover:scale-[1.01] bg-white ${
                  rightAds.length === 1 ? "h-full" : "h-[calc(50%-0.5rem)]"
                }`}
              >
                {/* Clean Image Area */}
                <div className="relative flex-1 w-full min-h-0 overflow-hidden bg-gray-100">
                  <img
                    src={ad.imageUrl}
                    alt={ad.title || "Ad Banner"}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wider bg-black/60 text-white px-2 py-0.5 rounded backdrop-blur-sm">
                    Sponsored
                  </span>
                </div>

                {/* Content & Action Below Image */}
                <div className="p-3 bg-white flex flex-col justify-between border-t border-gray-100">
                  <div>
                    <Typography variant="subtitle1" className="font-bold leading-tight text-gray-900 line-clamp-2">
                      {ad.title}
                    </Typography>
                    {ad.subtitle && (
                      <Typography variant="body2" className="mt-1 text-gray-600 text-xs line-clamp-2">
                        {ad.subtitle}
                      </Typography>
                    )}
                  </div>
                  <button className="w-full bg-gray-900 text-white font-medium py-1.5 px-3 rounded text-xs hover:bg-gray-800 transition mt-3">
                    Explore
                  </button>
                </div>
              </Paper>
            ))}
          </aside>
        )}

      </div>
    </div>
  );
};

export default CustomerLayout;